// ============================================================================
// Publishing an app (2026-10-08; docs/publishing.md): on a project with a repo,
// the form that puts its app on the cluster, and the taps that take a version
// live or off. Nothing here writes to a repo: the server keeps the list
// (internal/api/apps.go) and one ArgoCD ApplicationSet, installed once, makes
// each live app from the cluster's own chart. Online only; the list is the
// server's, not synced.
// ============================================================================
import { trackUse } from '@/state/usage'
import { useState } from 'react'
import { ExternalLink, Rocket } from 'lucide-react'
import { api } from '@/runtime'
import type { Build, PublishedApp } from '@/db/api'
import { useOnline } from './ClusterBits'
import { Skeleton } from './Skeleton'
import { Button, Field, Segmented, Sheet, Tag } from './ui'

const loadApps = () => api.apps()
const loadBuilds = () => api.builds()

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)

/** "KEY=value" lines to env, and back. */
const envFromText = (t: string): Record<string, string> =>
  Object.fromEntries(
    t
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#') && l.includes('='))
      .map((l) => [l.slice(0, l.indexOf('=')).trim(), l.slice(l.indexOf('=') + 1).trim()]),
  )
const envToText = (e?: Record<string, string>) =>
  Object.entries(e ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join('\n')

export const hostOf = (a: Pick<PublishedApp, 'subdomain' | 'access'>) =>
  `${a.subdomain}.${a.access === 'tailnet' ? 'mermaid-great.ts.net' : 'alfiemills.co.uk'}`

export const PublishSection = ({ projectId, repos }: { projectId: string; repos: string[] }) => {
  const apps = useOnline(loadApps, 30_000)
  const [editing, setEditing] = useState<PublishedApp | 'new' | null>(null)
  const repoNames = repos.map((r) => r.replace(/^https?:\/\/[^/]+\//, '').replace(/\.git$/, ''))
  const mine = (apps.data?.apps ?? []).filter((a) => a.project_id === projectId || repoNames.includes(a.repo))
  return (
    <section className="mb-4" data-testid="project-publish">
      <div className="flex items-center gap-2 mb-1.5">
        <h2 className="text-[13px] font-semibold text-muted flex-1">Published</h2>
        {apps.data && (
          <Button variant="chip" onClick={() => setEditing('new')} data-testid="publish-new">
            <Rocket size={14} aria-hidden /> Publish
          </Button>
        )}
      </div>
      {!apps.data ? (
        apps.error ? (
          <p className="text-sm text-muted px-1">{apps.error}</p>
        ) : (
          <Skeleton className="h-14" />
        )
      ) : mine.length === 0 ? (
        <p className="text-sm text-muted px-1">
          Not on the cluster. Publish puts it on its own subdomain, from a build of main you choose.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {mine.map((a) => (
            <AppCard key={a.name} app={a} onEdit={() => setEditing(a)} onChanged={apps.reload} />
          ))}
        </ul>
      )}
      {editing && (
        <PublishSheet
          app={editing === 'new' ? undefined : editing}
          projectId={projectId}
          repos={repoNames}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            apps.reload()
          }}
        />
      )}
    </section>
  )
}

/** One published app: where it answers, what runs, and the taps that change that. */
const AppCard = ({
  app,
  onEdit,
  onChanged,
}: {
  app: PublishedApp
  onEdit: () => void
  onChanged: () => void
}) => {
  const builds = useOnline(loadBuilds, 10_000)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const mineBuilds = (builds.data?.builds ?? []).filter((b: Build) => b.repo === app.repo).slice(0, 4)
  const newest = mineBuilds.find((b) => b.state === 'built')
  const building = mineBuilds.find((b) => b.state === 'queued' || b.state === 'building')
  const host = hostOf(app)
  const run = (what: string, fn: () => Promise<unknown>) => async () => {
    setBusy(what)
    setError('')
    try {
      await fn()
      onChanged()
      builds.reload()
    } catch (e) {
      setError((e as Error).message || 'That did not work')
    } finally {
      setBusy('')
    }
  }
  return (
    <li className="card p-3" data-testid={`app-${app.name}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{app.name}</p>
          {app.live ? (
            <a
              href={`https://${host}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-accent-text inline-flex items-center gap-1 break-all"
            >
              {host} <ExternalLink size={12} aria-hidden />
            </a>
          ) : (
            <p className="text-sm text-muted break-all">{host}</p>
          )}
        </div>
        <Tag tone={app.live ? 'done' : 'muted'}>{app.live ? 'Live' : app.tag ? 'Off' : 'Not live yet'}</Tag>
      </div>
      {/* phrases set apart, never strung together with dots */}
      <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-faint mt-1">
        <span>{app.access === 'tailnet' ? 'Tailnet only' : 'Public, through Cloudflare'}</span>
        <span>Port {app.port}</span>
        <span>Health {app.health}</span>
        {app.tag && <span>Runs {app.tag}</span>}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {newest && (newest.tag !== app.tag || !app.live) && (
          <Button
            variant="primary"
            onClick={run('ship', async () => {
              await api.appShip(app.name, newest.tag)
              trackUse('action:ship')
            })}
            disabled={!!busy}
            data-testid="app-ship"
          >
            {busy === 'ship' ? 'Going live…' : `Go live with ${newest.tag}`}
          </Button>
        )}
        {building ? (
          <Tag tone="running">Building {building.tag}</Tag>
        ) : (
          <Button
            variant="chip"
            onClick={run('build', () => api.appBuild(app.name))}
            disabled={!!busy}
            data-testid="app-build"
          >
            {busy === 'build' ? 'Starting…' : 'Build main now'}
          </Button>
        )}
        <Button variant="chip" onClick={onEdit} disabled={!!busy}>
          Edit
        </Button>
        {app.live ? (
          <Button
            variant="chip"
            onClick={() =>
              confirm(`Take ${app.name} off the cluster? It can go live again with one tap.`) &&
              void run('off', () => api.appUnpublish(app.name))()
            }
            disabled={!!busy}
            data-testid="app-unpublish"
          >
            Unpublish
          </Button>
        ) : (
          <Button
            variant="chip"
            onClick={() =>
              confirm(`Forget ${app.name}? Publishing it again starts from the form.`) &&
              void run('forget', () => api.appDelete(app.name))()
            }
            disabled={!!busy}
          >
            Forget
          </Button>
        )}
      </div>
      {!newest && !building && builds.data && (
        <p className="text-xs text-muted mt-2">
          No build of {app.repo} yet. It needs a Dockerfile serving on port {app.port}; build main to make
          one.
        </p>
      )}
      {error && <p className="text-xs text-rose-300 mt-2">{error}</p>}
    </li>
  )
}

/** The publish form: a new app for this project, or how one is published. */
const PublishSheet = ({
  app,
  projectId,
  repos,
  onClose,
  onSaved,
}: {
  app?: PublishedApp
  projectId: string
  repos: string[]
  onClose: () => void
  onSaved: () => void
}) => {
  const firstRepo = repos[0] ?? ''
  const [name, setName] = useState(app?.name ?? slug(firstRepo.split('/')[1] ?? ''))
  const [repo, setRepo] = useState(app?.repo ?? firstRepo)
  const [subdomain, setSubdomain] = useState(app?.subdomain ?? '')
  const [access, setAccess] = useState<'public' | 'tailnet'>(app?.access ?? 'public')
  const [port, setPort] = useState(String(app?.port ?? 8080))
  const [health, setHealth] = useState(app?.health ?? '/healthz')
  const [env, setEnv] = useState(envToText(app?.env))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const sub = subdomain || name
  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await api.appPut(name, {
        repo,
        project_id: projectId,
        subdomain: sub,
        access,
        port: Number(port),
        health,
        env: envFromText(env),
      })
      onSaved()
    } catch (e) {
      setError((e as Error).message || 'It was not saved')
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={app ? `Publish ${app.name}` : 'Publish an app'}
      footer={
        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-xs text-rose-300 mr-auto">{error}</span>}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            disabled={busy || !name || !repo}
            data-testid="publish-save"
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <Field label="Name" hint="Its name on the cluster; lowercase letters, digits and dashes">
        <input
          value={name}
          onChange={(e) => setName(slug(e.target.value))}
          disabled={!!app}
          className="w-full"
          data-testid="publish-name"
        />
      </Field>
      {repos.length > 1 && (
        <Field label="Repo">
          <select value={repo} onChange={(e) => setRepo(e.target.value)} className="w-full">
            {repos.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Who can reach it">
        <Segmented
          label="Access"
          value={access}
          onChange={setAccess}
          options={[
            { value: 'public', label: 'Anyone (public)' },
            { value: 'tailnet', label: 'Only my tailnet' },
          ]}
        />
      </Field>
      <Field label="Subdomain" hint={`Answers at ${hostOf({ subdomain: sub || 'name', access })}`}>
        <input
          value={subdomain}
          placeholder={name}
          onChange={(e) => setSubdomain(slug(e.target.value))}
          className="w-full"
          data-testid="publish-subdomain"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Port" hint="The one its server listens on">
          <input
            value={port}
            onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="w-full"
          />
        </Field>
        <Field label="Health path" hint="Answers 2xx when it is up">
          <input value={health} onChange={(e) => setHealth(e.target.value)} className="w-full" />
        </Field>
      </div>
      <Field label="Environment" hint="KEY=value, one a line. No secrets: those are refused.">
        <textarea
          value={env}
          onChange={(e) => setEnv(e.target.value)}
          rows={3}
          className="w-full font-mono text-[13px]"
          placeholder="LOG_LEVEL=info"
        />
      </Field>
      <p className="text-xs text-muted">
        Saving changes nothing on the cluster until a build goes live. The app runs from its repo's main,
        built by the factory from its Dockerfile.
      </p>
    </Sheet>
  )
}
